import * as D from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { CornerDownLeft, MessageSquare, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useNav } from "@/hooks/useNav";
import type { ConversationSummary } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Kbd } from "./ui/misc";
import { t } from "@shared/i18n";

type Item = { id: string; label: string; hint?: string; icon: React.ReactNode; run: () => void; group: string };

/** Recherche globale et palette de commandes (Ctrl/⌘ + K). */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const NAV = useNav();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [debounced, setDebounced] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const convs = useQuery({
    queryKey: ["conversations", "search", debounced],
    queryFn: async () => (await api<{ data: ConversationSummary[] }>(`/api/conversations?q=${encodeURIComponent(debounced)}`)).data,
    enabled: open && debounced.length >= 2,
  });

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const items = useMemo<Item[]>(() => {
    const needle = q.trim().toLowerCase();
    const actions: Item[] = [
      { id: "new-chat", group: t("Actions"), label: t("Nouvelle conversation avec le Coach"), icon: <Plus />, run: () => go("/app/coach?new=1") },
      { id: "new-task", group: t("Actions"), label: t("Créer une tâche"), icon: <Plus />, run: () => go("/app/tasks?new=1") },
      { id: "plan-day", group: t("Actions"), label: t("Planifier ma journée en un clic"), icon: <Plus />, run: () => go("/app/planning") },
      { id: "mission", group: t("Actions"), label: t("Lancer une mission (de l'idée à l'exécution)"), icon: <Plus />, run: () => go("/app/missions") },
      { id: "overdue", group: t("Actions"), label: t("Voir les tâches en retard"), icon: <Plus />, run: () => go("/app/tasks?view=overdue") },
    ];
    const pages: Item[] = NAV.map((n) => ({
      id: `nav-${n.id}`,
      group: t("Aller à"),
      label: n.label,
      hint: n.phase ? t("Phase {0}", n.phase) : undefined,
      icon: <n.icon />,
      run: () => go(n.to),
    }));
    const results: Item[] = (convs.data ?? []).slice(0, 8).map((c) => ({
      id: `conv-${c.id}`,
      group: t("Conversations"),
      label: c.title,
      icon: <MessageSquare />,
      run: () => go(`/app/coach/${c.id}`),
    }));
    const filter = (list: Item[]) => (needle ? list.filter((i) => i.label.toLowerCase().includes(needle)) : list);
    return [...filter(actions), ...filter(pages), ...results];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, convs.data, NAV]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[active]?.run();
    }
  };

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  let lastGroup = "";
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]" />
        <D.Content
          className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 overflow-hidden glass-card shadow-card"
          onKeyDown={onKeyDown}
        >
          <D.Title className="sr-only">{t("Recherche et commandes")}</D.Title>
          <D.Description className="sr-only">{t("Rechercher une page, une conversation ou lancer une action.")}</D.Description>
          <div className="flex items-center gap-3 border-b border-border px-4">
            <Search className="size-4 text-subtle" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("Rechercher une page, une conversation, une action…")}
              className="h-13 w-full bg-transparent text-[15px] outline-none placeholder:text-subtle"
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-list"
              aria-activedescendant={items[active] ? `palette-${items[active].id}` : undefined}
            />
          </div>
          <div ref={listRef} id="palette-list" role="listbox" className="scrollbar-thin max-h-[55vh] overflow-y-auto p-2">
            {items.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-muted">{convs.isFetching ? t("Recherche…") : t("Aucun résultat.")}</p>
            )}
            {items.map((item, i) => {
              const header = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <div key={item.id}>
                  {header && <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-subtle">{header}</p>}
                  <button
                    id={`palette-${item.id}`}
                    data-index={i}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={item.run}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm [&_svg]:size-4",
                      i === active ? "bg-primary-soft text-fg" : "text-fg",
                    )}
                  >
                    <span className="text-muted">{item.icon}</span>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && <span className="text-[12px] text-subtle">{item.hint}</span>}
                    {i === active && <CornerDownLeft className="text-subtle" />}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 border-t border-border px-4 py-2 text-[12px] text-subtle">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> naviguer <Kbd>{t("Entrée")}</Kbd> ouvrir <Kbd>{t("Échap")}</Kbd> fermer
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
